import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, ReplaySubject, from, merge, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { FetcherService } from './fetcher.service';
import { PlgTokenService } from './plg-token.service';
import { UserSessionStateService } from './user-session-state.service';

export interface PatchSessionBody {
    erp_id?: string;
    erp_name?: string;
    /** Send a string to lock the convention; send ``null`` to clear it
     * and let the backend re-run auto-detect on the next CoA trigger
     * (DR-48358). Omit the key entirely to leave the stored value
     * untouched — the backend distinguishes "key present with null"
     * from "key absent" via Pydantic's ``model_fields_set``. */
    amount_sign_convention?: string | null;
}

export interface SessionFile {
    file_id: string;
    filename: string;
    sheets: string[];
    selected_sheets: string[];
    fiscal_years: number[];
    is_demo: boolean;
}

export interface SessionResponse {
    session_id: string;
    step: number | null;
    erp_id: string | null;
    erp_name: string | null;
    amount_sign_convention: string | null;
    files: SessionFile[];
    mapping: { status: string } | null;
    coa: { status: string } | null;
    chat_questions_asked: number;
}

export interface AppConfig {
    session_ttl_sec: number;
    mapping_attempts_max: number;
    coa_attempts_max: number;
    chat_question_limit: number;
}

export interface StartChatResponse {
    export_blob_path: string;
    export_row_count: number;
}

/**
 * Handles all HTTP session operations and owns the shared observable cache
 * (`session$`, `config$`) that is eagerly populated on app startup.
 *
 * localStorage helpers live in UserSessionStateService so that HTTP interceptors
 * can inject that lighter service without triggering the circular DI chain:
 *   HTTP_INTERCEPTORS → [this service] → FetcherService → HttpClient → HTTP_INTERCEPTORS
 */
@Injectable({ providedIn: 'root' })
export class UserSessionService {
    private readonly state = inject(UserSessionStateService);
    private readonly fetcher = inject(FetcherService);
    private readonly plgToken = inject(PlgTokenService);

    // ── Edit-setup mode (in-memory only — cleared on refresh) ───────────────────
    private _editSetupMode = false;

    enterEditSetupMode(): void {
        this._editSetupMode = true;
    }

    isEditSetupMode(): boolean {
        return this._editSetupMode;
    }

    clearEditSetupMode(): void {
        this._editSetupMode = false;
    }

    // ── Shared app-init cache ─────────────────────────────────────────────────

    /**
     * Emits the session snapshot once loaded; emits `null` when there is no
     * active session or the request fails.
     * ReplaySubject(1) ensures late subscribers get the cached value synchronously.
     */
    private readonly _session$ = new ReplaySubject<SessionResponse | null>(1);
    readonly session$ = this._session$.asObservable();

    /**
     * Emits the server-side feature config once loaded; emits `null` on failure.
     */
    private readonly _config$ = new ReplaySubject<AppConfig | null>(1);
    readonly config$ = this._config$.asObservable();

    constructor() {
        // No circular dep here: interceptors inject UserSessionStateService, not this
        // service, so FetcherService → HttpClient → HTTP_INTERCEPTORS resolves cleanly.
        this._initCache();

        this.state.sessionInvalidated$.subscribe(() => this._session$.next(null));
    }

    private _initCache(): void {
        const sessionFetch$ = (
            this.state.hasSession()
                ? this.fetcher.get<SessionResponse>(`/api/v1/sessions/${this.state.getSessionId()}`)
                : of(null as SessionResponse | null)
        ).pipe(
            catchError(() => of(null as SessionResponse | null)),
            tap(session => this._session$.next(session)),
        );

        const configFetch$ = this.fetcher.get<AppConfig>('/api/v1/config').pipe(
            catchError(() => of(null as AppConfig | null)),
            tap(config => this._config$.next(config)),
        );

        merge(sessionFetch$, configFetch$).subscribe();
    }

    // ── LocalStorage helpers (delegated) ──────────────────────────────────────

    hasSession(): boolean {
        return this.state.hasSession();
    }
    getSessionId(): string | null {
        return this.state.getSessionId();
    }
    saveSessionId(id: string): void {
        this.state.saveSessionId(id);
    }
    clearSession(): void {
        this.state.clearSession();
    }
    resetState(): void {
        this.state.resetState();
    }
    isOnboardingCompleted(): boolean {
        return this.state.isOnboardingCompleted();
    }
    completeOnboarding(): void {
        this.state.completeOnboarding();
    }
    clearOnboardingCompleted(): void {
        this.state.clearOnboardingCompleted();
    }

    // ── API operations ─────────────────────────────────────────────────────────

    /**
     * Mints a fresh PLG session bound to the verified HubSpot token.
     *
     * The actual POST /api/v1/sessions request (with `Authorization: Bearer …`)
     * lives in `PlgTokenService.bootstrapSession()`, which also de-duplicates
     * concurrent callers. We delegate so there is exactly one place that knows
     * how to mint a session, regardless of whether it's the app-init flow or
     * the "Change ERP" reset flow.
     *
     * If the failure already raised `signUpRequired$`, complete the stream
     * via `EMPTY` so wizard-level error handlers (e.g. the "Failed to
     * proceed" toast) don't fire on top of the in-app sign-up landing, and
     * so any `finalize()` blocks (loading flags, subscription cleanup) still
     * run. Genuine errors that didn't trip the sign-up gate still propagate.
     */
    createSession(): Observable<string> {
        return from(this.plgToken.bootstrapSession()).pipe(
            catchError(err => (this.plgToken.signUpRequired ? EMPTY : throwError(() => err))),
        );
    }

    deleteSession(): Observable<void> {
        return this.fetcher.delete<void>(`/api/v1/sessions/${this.state.getSessionId()}`);
    }

    getSession(): Observable<SessionResponse> {
        return this.fetcher.get<SessionResponse>(`/api/v1/sessions/${this.state.getSessionId()}`);
    }

    /**
     * Re-fetches the session and pushes it into the shared `session$` cache.
     * Use when a screen detects its local state has diverged from the server
     * (e.g. files deleted, mapping invalidated) and the wizard parent needs
     * to re-restore + remount the correct step.
     *
     * On error the stream completes via `EMPTY` rather than emitting `null`:
     * a transient network/5xx must not poison the cached `session$` with
     * `null` and force the wizard back to step 0 when a valid server-side
     * session still exists. Callers see no `next` emission on failure; the
     * 401-recovery path is owned by `SessionRetryInterceptor` separately.
     */
    refetchSession(): Observable<SessionResponse> {
        return this.getSession().pipe(
            tap(session => this._session$.next(session)),
            catchError(() => EMPTY),
        );
    }

    patchSession(body: PatchSessionBody): Observable<void> {
        return this.fetcher.patch<void>(`/api/v1/sessions/${this.state.getSessionId()}`, body);
    }

    startChat(): Observable<StartChatResponse> {
        return this.fetcher.post<StartChatResponse>(`/api/v1/sessions/${this.state.getSessionId()}/onboarding`, null);
    }
}
